/**
 * RM-W1-09 (OR-08): every sentence this feature uses to state the publication
 * contract, in one place.
 *
 * The contract is that raw inputs stay private and generated content, which may
 * quote them, becomes public only after the owner has previewed the exact artifact.
 * Three surfaces make that promise: the setup page before anything is written, the
 * publish dialog at the moment of confirmation, and the owner's report view
 * afterwards. If those three drift apart, the feature is making three different
 * promises and at most one of them is the one the code keeps.
 *
 * The two fallback-band sentences live next door in `scenario-provenance.ts`,
 * beside the predicates that decide which of the two applies, because choosing
 * between them is a rule rather than a wording choice.
 */

/** The setup page, beside the thesis box, before a single word is submitted. */
export const THESIS_PUBLICATION_DISCLOSURE =
  "Your thesis stays private. If you publish this simulation, the generated debate and report, which may quote your thesis, become public. You can preview exactly what will be public before publishing.";

/** The sentence the owner must actively confirm before anything is published. */
export const PUBLISH_DISCLOSURE =
  "I understand that publishing makes the generated debate, report and summary public to anyone with the link, and that this generated text may quote or reflect my thesis. My thesis itself is never published.";

/**
 * The irreversibility residual, disclosed at the point of decision rather than only
 * in canon 07. Unpublishing is immediate at our origin; it is not a recall.
 */
export const PUBLISH_RESIDUAL_NOTICE =
  "Unpublishing takes the page down at once, but copies already fetched by other services, such as link previews, browser caches and search engines, cannot be recalled.";

/** The stored content cannot produce a valid public artifact (RM-W1-08's 422). */
export const PUBLICATION_BLOCKED_NOTICE =
  "This simulation cannot be published until its stored content is repaired.";

/**
 * H3-N01: a published row whose stored content has since become invalid. Public
 * reads already refuse it, but the row is still MARKED published, so if its content
 * ever returned to the exact confirmed bytes it would be served again. Withdrawal
 * clears that mark, and this is the sentence that offers it.
 */
export const WITHDRAW_INVALID_PUBLICATION_NOTICE =
  "This simulation is still marked published. Nobody can read it while its content fails these checks, but you can withdraw the publication so it stays private until you choose to publish again.";

/** A published row whose content or serialiser has moved on since it was confirmed. */
export const PUBLICATION_STALE_NOTICE =
  "Publication out of date: the content or the serialiser changed; preview and confirm again.";

/** The server refused the previewed token; a fresh artifact is now on screen. */
export const STALE_PREVIEW_NOTICE =
  "This simulation changed since you previewed it, so nothing was published. Below is the current version. Read it and confirm again.";

/** The artifact carries no round log, which is a publication state, not an error. */
export const ROUNDS_WITHHELD_NOTICE =
  "The round-by-round log is not part of this publication.";
